import { useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import { Play, SkipBack, SkipForward, Film, Music } from "lucide-react"
import { cn } from "@/lib/utils"
import { useScrollSequence } from "./shared"

type TimelineStep = "idle" | "track" | "clip1" | "clip2" | "playhead" | "highlight"

const CLIPS = [
    { label: "1", gradient: "from-amber-400 via-orange-500 to-rose-500" },
    { label: "2", gradient: "from-cyan-400 via-blue-500 to-purple-600" },
]

const TICK_MARKS = ["0:00", "0:05", "0:10"]

const STEPS: TimelineStep[] = ["track", "clip1", "clip2", "playhead", "highlight"]
const SEQUENCE = [
    { delay: 300 },
    { delay: 400 },
    { delay: 400 },
    { delay: 300 },
    { delay: 1200 },
]

const ORDER: TimelineStep[] = ["idle", "track", "clip1", "clip2", "playhead", "highlight"]

export function IntegrationBlock({ popoverPosition: _popoverPosition = "top" }: { popoverPosition?: "top" | "bottom" }) {
    const [step, setStep] = useState<TimelineStep>("idle")

    const { ref } = useScrollSequence(
        SEQUENCE,
        (i) => setStep(STEPS[i]),
        () => setStep("idle"),
        { amount: 0.6, margin: "40px 0px -40px 0px" },
    )

    const si = ORDER.indexOf(step)
    const showTrack = si >= 1
    const clipVisible = [si >= 2, si >= 3]
    const showPlayhead = si >= 4
    const showHighlight = si >= 5

    return (
        <div
            ref={ref}
            className="relative flex items-center justify-center px-6 md:px-12 pt-6 md:pt-12 pb-2 overflow-visible"
        >
            <div className="w-full max-w-lg bg-neutral-900 rounded-xl border border-neutral-800 overflow-hidden">
                {/* Toolbar */}
                <div className="flex items-center gap-2 px-2 h-8 border-b border-neutral-800 bg-neutral-950/80">
                    <div className="flex items-center shrink-0 border border-neutral-700 bg-neutral-950/80 h-5">
                        <button className="grid h-full w-5 place-items-center text-neutral-400 border-r border-neutral-700">
                            <SkipBack size={10} />
                        </button>
                        <button className="grid h-full w-5 place-items-center text-neutral-200 border-r border-neutral-700">
                            <Play size={10} className="fill-neutral-200" />
                        </button>
                        <button className="grid h-full w-5 place-items-center text-neutral-400">
                            <SkipForward size={10} />
                        </button>
                    </div>
                    <span className="text-[10px] text-neutral-400 font-mono tabular-nums">0:00</span>
                    <div className="flex-1" />
                    <span className="text-[10px] text-neutral-500 font-mono tabular-nums">0:20</span>
                </div>

                {/* Track area */}
                <div className="flex min-h-[100px]">
                    {/* Track labels */}
                    <div className="shrink-0 w-14 bg-neutral-900/80 border-r border-neutral-800">
                        <div className="h-5 border-b border-neutral-800" />
                        <div className="h-16 border-b border-neutral-800 flex items-center gap-1 px-1.5">
                            <Film size={10} className="text-neutral-400" />
                            <span className="text-[9px] text-neutral-400">Video</span>
                        </div>
                        <div className="h-10 border-b border-neutral-800 flex items-center gap-1 px-1.5">
                            <Music size={10} className="text-neutral-400" />
                            <span className="text-[9px] text-neutral-400">Audio</span>
                        </div>
                    </div>

                    {/* Timeline content */}
                    <div className="flex-1 relative">
                        {/* Time ruler */}
                        <div className="h-5 border-b border-neutral-800 bg-neutral-900 relative">
                            {TICK_MARKS.map((mark, i) => (
                                <div
                                    key={mark}
                                    className="absolute top-0 bottom-0"
                                    style={{ left: `${(i / (TICK_MARKS.length - 1)) * 100}%` }}
                                >
                                    <div className="h-full w-px bg-neutral-800" />
                                    <span className="absolute top-0.5 left-1 text-[9px] text-neutral-400 font-mono tabular-nums whitespace-nowrap">
                                        {mark}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {/* Video track */}
                        <div className="h-16 border-b border-neutral-800 relative bg-[#1a1a1a]">
                            <AnimatePresence>
                                {showTrack && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.2 }}
                                        className="absolute inset-1 flex gap-px"
                                    >
                                        {CLIPS.map((clip, i) => (
                                            <AnimatePresence key={i}>
                                                {clipVisible[i] && (
                                                    <motion.div
                                                        initial={{ opacity: 0, scaleX: 0 }}
                                                        animate={{ opacity: 1, scaleX: 1 }}
                                                        exit={{ opacity: 0, scaleX: 0 }}
                                                        transition={{ duration: 0.3, ease: "easeOut" }}
                                                        style={{ originX: 0 }}
                                                        className={cn(
                                                            "relative rounded overflow-hidden flex-1 border",
                                                            showHighlight && i === 1
                                                                ? "border-sky-500 ring-2 ring-sky-400/40"
                                                                : "border-neutral-700/80"
                                                        )}
                                                    >
                                                        <div className={cn("h-full w-full bg-gradient-to-r", clip.gradient)} />
                                                        <div className="absolute top-0 left-0 bg-black/50 text-white text-[8px] px-1 py-px font-mono leading-tight rounded-br">
                                                            {clip.label}
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        ))}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Audio track */}
                        <div className="h-10 border-b border-neutral-800 relative bg-neutral-900/50">
                            <AnimatePresence>
                                {showTrack && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.3, delay: 0.3 }}
                                        className="absolute inset-x-1 top-1 bottom-1"
                                    >
                                        <div className="relative w-full h-full rounded border border-neutral-700/60 bg-neutral-800/40 overflow-hidden">
                                            <div className="absolute inset-0 flex items-center px-1 gap-px">
                                                {Array.from({ length: 60 }).map((_, i) => (
                                                    <div
                                                        key={i}
                                                        className="flex-1 bg-purple-500/40 rounded-sm"
                                                        style={{ height: `${Math.max(10, Math.min(90, 20 + Math.sin(i * 0.4) * 30 + Math.sin(i * 1.1) * 20 + Math.cos(i * 0.7) * 15))}%` }}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Playhead */}
                        <AnimatePresence>
                            {showPlayhead && (
                                <motion.div
                                    initial={{ left: "4px" }}
                                    animate={{ left: showHighlight ? "45%" : "4px" }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 1.2, ease: "easeInOut" }}
                                    className="absolute top-5 bottom-0 z-20 pointer-events-none"
                                >
                                    <div className="absolute -left-px top-0 bottom-0 w-0.5 bg-red-500" />
                                    <div
                                        className="absolute -left-[4px] -top-px"
                                        style={{
                                            width: 0, height: 0,
                                            borderLeft: "4px solid transparent",
                                            borderRight: "4px solid transparent",
                                            borderTop: "5px solid #ef4444",
                                        }}
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </div>
    )
}
